import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Bell,
  CheckCheck,
  Clock,
  FileCheck2,
  MapPin,
  Megaphone,
  MessageSquareWarning,
  Settings as SettingsIcon,
} from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { useInvalidateEmployeeUnread } from "@/hooks/useUnreadNotifications";
import { ApiError } from "@/lib/api";
import { formatDateTimeIn } from "@/lib/datetime";
import {
  employeeApi,
  type EmployeeConfig,
  type NotificationItem,
  type NotificationType,
} from "@/lib/employee-api";

const PAGE_SIZE = 30;

function iconFor(t: NotificationType) {
  switch (t) {
    case "MISSED_ATTENDANCE":
      return AlertTriangle;
    case "MISSED_CHECK_OUT":
      return Clock;
    case "OUTSIDE_GEOFENCE":
      return MapPin;
    case "REQUEST_APPROVED":
    case "APPROVAL":
      return CheckCheck;
    case "REQUEST_REJECTED":
    case "REQUEST_NEEDS_CLARIFICATION":
      return MessageSquareWarning;
    case "PENDING_REQUEST":
      return FileCheck2;
    case "ANNOUNCEMENT":
      return Megaphone;
    case "CHECK_IN_REMINDER":
      return Clock;
    default:
      return SettingsIcon;
  }
}

/**
 * Turn a notification's `actionLink` into somewhere this browser can actually
 * go.
 *
 * Notifications are written once and read on both surfaces, so `actionLink`
 * carries the mobile deep link (`app://attendance/2026-08-25`). Following that
 * in a browser does nothing, and ignoring the date and opening "today" instead
 * is worse — it shows a valid record beside a warning that there is none,
 * which is precisely the confusion QA reported. So translate the date through
 * rather than dropping it, and return null when there is no sensible web
 * destination, so the row simply isn't clickable.
 */
function webDestination(link: string | null): string | null {
  if (!link) return null;
  if (link.startsWith("/")) return link;
  const attendance = /^app:\/\/attendance\/(\d{4}-\d{2}-\d{2})$/.exec(link);
  if (attendance) return `/me/attendance?date=${attendance[1]}`;
  if (/^app:\/\/requests?\b/.test(link)) return "/me/requests";
  if (/^app:\/\/schedule\b/.test(link)) return "/me/schedule";
  return null;
}

export default function MyNotifications() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const invalidateUnread = useInvalidateEmployeeUnread();
  const [config, setConfig] = useState<EmployeeConfig | null>(null);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tz = config?.tenant.timezone ?? "Asia/Kuala_Lumpur";
  const unread = items.filter((n) => !n.isRead).length;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, res] = await Promise.all([
        employeeApi.config(),
        employeeApi.notifications({ pageSize: PAGE_SIZE }),
      ]);
      setConfig(cfg);
      setItems(res.data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load your notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const open = async (n: NotificationItem) => {
    const to = webDestination(n.actionLink);
    if (!n.isRead) {
      // Optimistic: the badge should drop the moment it's tapped, and a failed
      // mark-read is not worth blocking navigation over.
      setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, isRead: true } : i)));
      try {
        await employeeApi.markNotificationRead(n.id);
        invalidateUnread();
      } catch {
        setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, isRead: false } : i)));
      }
    }
    if (to) navigate(to);
  };

  const markAll = async () => {
    try {
      await employeeApi.markAllNotificationsRead();
      setItems((prev) => prev.map((i) => ({ ...i, isRead: true })));
      invalidateUnread();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not mark them read",
        description: e instanceof ApiError ? e.message : "Something went wrong",
      });
    }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Couldn't load your notifications</AlertTitle>
        <AlertDescription className="flex items-center gap-3">
          {error}
          <Button size="sm" variant="outline" onClick={() => void load()}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      {unread > 0 && (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => void markAll()}>
            <CheckCheck className="mr-1.5 h-4 w-4" />
            Mark all read
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="divide-y p-0">
          {items.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <Bell className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Nothing to catch up on.</p>
            </div>
          )}
          {items.map((n) => {
            const Icon = iconFor(n.type);
            const to = webDestination(n.actionLink);
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => void open(n)}
                className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary ${
                  to ? "cursor-pointer" : "cursor-default"
                }`}
              >
                <Icon
                  className={`mt-0.5 h-4 w-4 shrink-0 ${
                    n.severity === "CRITICAL"
                      ? "text-destructive"
                      : n.severity === "WARNING"
                        ? "text-warning"
                        : "text-muted-foreground"
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`truncate text-sm ${n.isRead ? "font-normal" : "font-semibold"}`}
                    >
                      {n.title}
                    </span>
                    {!n.isRead && <Badge variant="secondary">New</Badge>}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{n.body}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {formatDateTimeIn(n.createdAt, tz)}
                  </p>
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
